
import { Suspense, lazy, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import PageTransition from "@/components/ui/page-transition";
import ScrollProgress from "@/components/ui/scroll-progress";
// import SuggestionSystem from "@/components/intelligent-suggestions/SuggestionSystem";
import Navbar from "@/components/layout/Navbar";
import HomeNavigation from "@/components/home/HomeNavigation";
import TourControls from "@/components/ui/TourControls";
import { ScrollProvider } from "@/hooks/useSmoothScroll";
import { TourProvider, useTour } from "@/hooks/useTour";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { publicRoutes } from "./pages/public-routes";

const PresentationPage = lazy(() => import("./pages/PresentationPage"));
const PresentationRemote = lazy(() => import("./pages/PresentationRemote"));
const VideoPage = lazy(() => import("./pages/VideoPage"));
const VideoPage1 = lazy(() => import("./pages/VideoPage1"));
const VideoPage2 = lazy(() => import("./pages/VideoPage2"));
const NotFound = lazy(() => import("./pages/NotFound"));

const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage"));
const EbookDashboard = lazy(() => import("./pages/ebook/EbookDashboard"));
const EbookReaderPage = lazy(() => import("./pages/ebook/EbookReaderPage"));
const CheckoutPage = lazy(() => import("./pages/ebook/CheckoutPage"));
const DownloadsPageSimple = lazy(() => import("./pages/DownloadsPageSimple"));
const LpPage = lazy(() => import("./pages/LpPage"));
const VideoArticlesPage = lazy(() => import("./pages/VideoArticlesPage"));
const VideoSyncedArticlePage = lazy(() => import("./pages/VideoSyncedArticlePage"));
const PixPage = lazy(() => import("./pages/PixPage"));
// const BuilderPage = lazy(() => import("./pages/BuilderPage"));
// import ProximoEvento from "./pages/ProximoEvento";

import { ThemeProvider } from "next-themes";

const queryClient = new QueryClient();

import AnimatedBackground from '@/components/ui/AnimatedBackground';
// CustomCursor removido — causava efeito de lentidão no mouse
import TourProgressBar from '@/components/ui/TourProgressBar';
import { ConsentBanner } from '@/components/consent/ConsentBanner';
import ConditionalScripts from '@/components/consent/ConditionalScripts';
import { GtagConsentSync } from '@/components/analytics/GtagConsentSync';

const AppContent = () => {
  const location = useLocation();
  const [isChatOpen, setIsChatOpen] = useState(true);
  const { isTourActive, currentStep, currentStepDelay } = useTour();

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  const isEbookReaderPage = location.pathname.includes('/ebook/') && location.pathname.includes('/read');
  const isBlogPage = location.pathname.startsWith('/blog');
  
  return (
    <div className="min-h-screen relative">
      {/* CustomCursor removido */}
      {isTourActive && <TourProgressBar key={currentStep} duration={currentStepDelay} />}
      {/* AnimatedBackground desabilitado em páginas PULSE (dark) */}
      {/* AnimatedBackground desabilitado — site usa PULSE dark theme */}
      {isBlogPage && (
        <div className="fixed inset-0 pointer-events-none -z-10">
          <div className="absolute inset-0 bg-background/95 backdrop-blur-sm" style={{
            maskImage: 'radial-gradient(ellipse 85% 100% at 50% 50%, black 50%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 85% 100% at 50% 50%, black 50%, transparent 75%)'
          }} />
        </div>
      )}
      {!isEbookReaderPage && (location.pathname === '/' ? <HomeNavigation /> : <Navbar onToggleChat={toggleChat} isChatOpen={isChatOpen} />)}
      
      <Suspense
        fallback={
          <div className="flex min-h-[40vh] items-center justify-center">
            <span className="text-sm text-muted-foreground" role="status">
              Loading page…
            </span>
          </div>
        }
      >
        <PageTransition>
          <Routes>
            {/* Rotas públicas e indexáveis: declaradas em pages/public-routes.tsx, que o
                main.tsx usa para carregar a rota antes de hidratar o HTML pré-renderizado. */}
            {publicRoutes.map(({ path, Page }) => (
              <Route key={path} path={path} element={<Page />} />
            ))}
            <Route path="/presentation" element={<PresentationPage />} />
            <Route path="/remote" element={<PresentationRemote />} />
            <Route path="/video" element={<VideoPage />} />
            <Route path="/video1" element={<VideoPage1 />} />
            <Route path="/video2" element={<VideoPage2 />} />
            <Route path="/downloads" element={<DownloadsPageSimple />} />
            <Route path="/lp" element={<LpPage />} />
            <Route path="/pix" element={<PixPage />} />
            {/* <Route path="/builder" element={<BuilderPage />} /> */}

            {/* 🔄 Redirects: /video-articles → /blog (unificação) */}
            <Route path="/video-articles" element={<Navigate to="/blog" replace />} />
            <Route path="/video-articles/:id" element={<Navigate to="/blog/:id" replace />} />

            {/* 📌 Rotas antigas mantidas temporariamente para compatibilidade */}
            <Route path="/video-articles-legacy" element={<VideoArticlesPage />} />
            <Route path="/video-articles-legacy/:id" element={<VideoSyncedArticlePage />} />

            {/* <Route path="/proximo-evento" element={<ProximoEvento />} /> */}
            
            {/* Auth Routes */}
            <Route path="/auth/login" element={<LoginPage />} />
            
            {/* Protected Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Ebook Routes */}
            <Route 
              path="/ebook" 
              element={
                <ProtectedRoute>
                  <EbookDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/ebook/:ebookId/checkout" 
              element={
                <ProtectedRoute>
                  <CheckoutPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/ebook/:ebookId/read" 
              element={
                <ProtectedRoute>
                  <EbookReaderPage />
                </ProtectedRoute>
              } 
            />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageTransition>
      </Suspense>
      
      {/* Sistema de Sugestões Inteligentes */}
      {/* <SuggestionSystem 
        currentPage={location.pathname} 
        isChatOpen={isChatOpen}
        onToggleChat={toggleChat}
      /> */}

      {/* {isTourActive && (
        <TourControls
          onNext={nextStep}
          onPrev={prevStep}
          onExit={exitTour}
          currentStep={currentStep}
          totalSteps={totalSteps}
        />
      )} */}

      {/* LGPD Consent Banner */}
      <ConsentBanner />

      {/* Sincroniza consent LGPD com gtag Consent Mode v2 */}
      <GtagConsentSync />

      {/* Scripts condicionais - só carregam com consentimento */}
      <ConditionalScripts
        googleTagManagerId={import.meta.env.VITE_GTM_ID || 'GTM-5JXWNS7P'}
        facebookPixelId={import.meta.env.VITE_META_PIXEL_ID}
        analyticsScripts={[
          {
            id: 'metricool-tracker',
            inline: `function loadScript(a){var b=document.getElementsByTagName("head")[0],c=document.createElement("script");c.type="text/javascript",c.src="https://tracker.metricool.com/resources/be.js",c.onreadystatechange=a,c.onload=a,b.appendChild(c)}loadScript(function(){beTracker.t({hash:"111a627d6b18634724d1343a97d75e92"})});`,
          },
        ]}
      />
    </div>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
            <ScrollProgress />
            <ScrollProvider>
              <TourProvider>
                <AuthProvider>
                  <AppContent />
                </AuthProvider>
              </TourProvider>
            </ScrollProvider>
          </ThemeProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
